import type { CSSProperties, FC, ReactNode } from 'react';
import { Avatar } from 'antd';

import { useWorkspaceResourceWebsite } from '@/hooks/useWorkspaceResourceWebsite';
import { WorkspaceResourceAvatar } from '@/shared/components/workspace-resources';
import type { CommentMentionTarget } from '@/types';

interface CommentMentionIdentityProps {
    avatarSize?: number;
    description?: ReactNode;
    descriptionStyle?: CSSProperties;
    labelStyle?: CSSProperties;
    mention: CommentMentionTarget;
}

const CommentMentionIdentity: FC<CommentMentionIdentityProps> = ({
    avatarSize = 18,
    description,
    descriptionStyle,
    labelStyle,
    mention,
}) => {
    const resourceWebsite = useWorkspaceResourceWebsite(
        mention.kind === 'resource' ? mention.resourceType : undefined,
        mention.kind === 'resource' ? mention.id : undefined,
        mention.kind === 'resource' ? mention.website : undefined,
    );
    const avatar =
        mention.kind === 'member' ? (
            <Avatar size={avatarSize} src={mention.avatar} style={{ flex: 'none', fontSize: '0.625rem' }}>
                {mention.label.charAt(0).toUpperCase()}
            </Avatar>
        ) : (
            <WorkspaceResourceAvatar
                listType={mention.listType}
                resource={mention.kind === 'team' ? 'team' : (mention.resourceType ?? 'resource')}
                size={avatarSize}
                website={resourceWebsite}
            />
        );
    const compact = !description;

    return (
        <>
            <span
                style={{
                    alignItems: 'center',
                    display: 'inline-flex',
                    flex: 'none',
                    height: compact ? avatarSize : '1.25rem',
                    lineHeight: 0,
                }}
            >
                {avatar}
            </span>
            <span
                style={{
                    alignItems: compact ? 'center' : undefined,
                    display: compact ? 'inline-flex' : 'inline-block',
                    flex: '1 1 auto',
                    height: compact ? '1.25rem' : undefined,
                    lineHeight: compact ? '1.25rem' : undefined,
                    minWidth: 0,
                }}
            >
                <span
                    style={{
                        display: compact ? 'inline-block' : 'block',
                        lineHeight: compact ? '1.25rem' : undefined,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        ...labelStyle,
                    }}
                >
                    {mention.label}
                </span>
                {description && (
                    <small
                        style={{
                            display: 'block',
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            ...descriptionStyle,
                        }}
                    >
                        {description}
                    </small>
                )}
            </span>
        </>
    );
};

export default CommentMentionIdentity;
